import { useEffect, useMemo, useState } from "react";
import { SearchIcon } from "../../icons";
import GuideHint from "../GetStartedGuide/GuideHint";
import { GetAccountAssets } from "../../../redux/actions/CryptoTransaction/GetCurrencyNetwork";
import { useDispatch } from "react-redux";
import Skeleton from "../../../components/Common/Skeleton";


const AssetCard = ({ asset, selected = false, onClick }) => {
  const [assetImgError, setAssetImgError] = useState(false);
  const [networkImgError, setNetworkImgError] = useState(false);

  const networkLogo = asset.network?.logo_url;
  // const assetName = asset.name || asset.network.name || "AS";
  const assetName = asset.currency_short || asset.network.name || "AS";
  const assetSymbol = asset.name || asset.currency_name || "AS";
  const networkCode = asset.network?.code || "NW";

  return (
    <button
      type="button"
      onClick={onClick}
        className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-all duration-200 border ${
          selected
          ? "bg-[rgba(74,185,50,0.10)] border-2 border-primary-bg text-text-primary"
          : "hover:bg-[rgba(74,185,50,0.06)] bg-background-alt border-2 border-divider text-text-primary"
        }`}
    >
      {/* Avatar Container with Network Badge Overlay */}
      <div className="relative shrink-0">
        {/* Main Asset Image or Native Initial Avatar */}
        {asset.logo_url && !assetImgError ? (
          <img
            src={asset.logo_url}
            alt={assetSymbol}
            onError={() => setAssetImgError(true)}
            className="h-10 w-10 rounded-full object-contain bg-transparent p-0.5"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-transparent text-xs font-extrabold 
            uppercase text-text-primary select-none">
            {assetSymbol.slice(0, 3)}
          </div>
        )}

        {/* Small Network Logo Overlay on bottom-right of avatar */}
        {networkLogo && !networkImgError ? (
          <img
            src={networkLogo}
            alt={asset.network?.name}
            onError={() => setNetworkImgError(true)}
            className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-divider bg-transparent object-contain"
          />
        ) : (
          <div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border-2 
            border-divider bg-background-secondary text-[8px] font-bold uppercase text-text-secondary select-none">
            {networkCode.slice(0, 2)}
          </div>
        )}
      </div>

      {/* Main Info */}
      <div className="min-w-0 flex-1">
        {/* Title row: Code (Name) */}
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-bold text-text-primary">
            {assetName}
            {/* {assetSymbol} */}
          </span>

          {asset.network?.name && (
            <span className="truncate text-sm text-text-secondary font-normal">
              ({assetSymbol})
              {/* ({asset.network.name}) */}
            </span>
          )}
        </div>

        {/* Subtitle row: Network Name */}
        <p className="truncate text-xs text-text-secondary mt-0.5">
          {asset.network?.name || "Unknown Network"}
        </p>
      </div>
    </button>
  );
};

const AssetCardSkeletonItem = () => (
  <div className="flex w-full items-center gap-3 rounded-lg bg-background-alt px-3 py-2.5">
    {/* Avatar & Overlay Badge Skeleton */}
    <div className="relative shrink-0">
      <Skeleton className="h-10 w-10 rounded-full" />
      <Skeleton className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-background-surface" />
    </div>

    {/* Text Lines Skeleton */}
    <div className="flex flex-1 flex-col gap-1.5 min-w-0">
      {/* Title Row: Symbol (Network) */}
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-24 rounded" />
        <Skeleton className="h-3 w-32 rounded" />
      </div>

      {/* Subtitle Row: Network Name */}
      <Skeleton className="h-3 w-28 rounded" />
    </div>
  </div>
);

const ConfirmDepositAssetSkeleton = ({ count = 4 }) => (
  <div className="flex w-full flex-col gap-3">
    {Array.from({ length: count }).map((_, index) => (
      <AssetCardSkeletonItem key={index} />
    ))}
  </div>
);

/* Empty State Placeholder */
const EmptyAssets = () => (
  <div className="flex flex-1 flex-col items-center justify-center py-10 text-center">
    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background-alt text-text-secondary mb-2">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>
    </div>
    <p className="text-sm font-semibold text-text-primary">No assets found</p>
    <p className="mt-1 text-xs text-text-secondary">
      Try searching for a different token or network.
    </p>
  </div>
)

const ConfirmDepositAsset = ({
  selectedNetwork,
  onNetworkSelect,
  handleContinue,
  spotlightActive = false,
  guideHint = null,
  selectedAssetId = null,
  skipAutoContinue = false,
}) => {
  const [search, setSearch] = useState("");
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    let isMounted = true;

    const getAssets = async () => {
      try {
        setLoading(true);
        const fetchedAssets = await dispatch(
          GetAccountAssets({ account_id: selectedAssetId })
        );

        if (!isMounted) return;
        setAssets(fetchedAssets || []);

        // Only auto-proceed if there is exactly 1 asset AND we are NOT returning from back/cancel
        if (fetchedAssets?.length === 1) {
          const defaultNetwork = fetchedAssets[0];
          onNetworkSelect(defaultNetwork);

          if (!skipAutoContinue) {
            await handleContinue(defaultNetwork);
          }
        }
      } catch (error) {
        console.error("Error fetching account assets:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (selectedAssetId) {
      getAssets();
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line
  }, [selectedAssetId, skipAutoContinue]);

  const filteredAssets = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return assets ?? [];

    return (assets ?? []).filter((asset) => {
      return [
        asset?.asset_id,
        asset?.type,
        asset?.name ?? asset?.network?.name,
        asset?.code,
        // asset?.network?.name,
        // asset?.network?.code,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query));
    });
  }, [search, assets]);

  const assetsSection = (
    <>
    <div className="flex shrink-0 flex-col overflow-hidden rounded-[12px] border border-divider p-2">
      <div className="flex h-[360px] flex-col gap-2 pr-1.5 overflow-y-auto custom-scrollbar">
        {loading ? (
          <ConfirmDepositAssetSkeleton />
        ) : filteredAssets.length > 0 ? (
          filteredAssets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              selected={selectedNetwork?.id === asset?.id}
              onClick={() => onNetworkSelect(asset)}
            />
          ))
        ) : (
          <EmptyAssets />
        )}
      </div>
    </div>
    <button
      type="button"
      disabled={!selectedNetwork?.id}
      onClick={handleContinue}
      className="flex h-12 mb-2 w-full shrink-0 items-center justify-center rounded-[10px] bg-[#4ab933] 
      text-sm font-semibold leading-[21px] text-white custom-transition hover:bg-[#3fa82c] 
      disabled:cursor-not-allowed disabled:opacity-50"
    >
      Continue
    </button>
    </>
  );

  return (
    <div
      className={`flex flex-col gap-4 px-4 pt-4 sm:h-full sm:min-h-0 ${
        spotlightActive ? "max-sm:shrink-0" : "h-full min-h-0"
      }`}
    >
      <h3 className="shrink-0 px-2 text-lg font-semibold leading-7 text-text-primary">
        Confirm Asset
      </h3>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto custom-scrollbar">
        <div className="flex h-10 w-full shrink-0 items-center gap-2 rounded-[10px] border border-divider bg-background-alt py-2.5 pl-4 pr-3">
          <SearchIcon />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search asset network..."
            className="w-full border-0 bg-transparent p-0 text-xs font-normal leading-normal text-text-primary placeholder:text-text-secondary outline-none focus:border-0 focus:outline-none focus:ring-0"
          />
        </div>

        {spotlightActive ? (
          <div className="relative z-40 max-sm:shrink-0 max-sm:-mx-4 max-sm:rounded-t-[12px] max-sm:bg-background-surface max-sm:px-4 max-sm:pb-4 max-sm:pt-4 sm:flex sm:min-h-0 sm:flex-1 sm:flex-col sm:rounded-none sm:bg-transparent sm:p-0">
            {guideHint && (
              <div className="pointer-events-none absolute inset-x-4 bottom-full z-50 mb-2 sm:hidden">
                <div className="pointer-events-auto mx-auto w-[90%] max-w-[280px]">
                  <GuideHint
                    title={guideHint.title}
                    description={guideHint.description}
                    primaryAction={guideHint.primaryAction}
                    secondaryAction={guideHint.secondaryAction}
                    onSkip={guideHint.onSkip}
                    onDone={guideHint.onDone}
                    onBuyCrypto={guideHint.onBuyCrypto}
                  />
                </div>
              </div>
            )}
            {assetsSection}
          </div>
        ) : (
          assetsSection
        )}
      </div>
    </div>
  );
};

export default ConfirmDepositAsset;
